import { useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import SmartToyRoundedIcon from '@mui/icons-material/SmartToyRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import FingerprintRoundedIcon from '@mui/icons-material/FingerprintRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  applicationsApi,
  manualReviewApi,
  MANUAL_REVIEW_QUEUE_QUERY_KEY,
  type ApplicationJourneyHeaderDto,
  type ApplicationDeviceRiskResponseDto,
} from '../../api/admin';
import type { ApplicationTabKey } from '../../lib/applicationDetailTabStatus';
import { identitySessionHasPersistedEvidence } from '../../lib/applicationDetailTabStatus';
import { adminConsoleAuthApi, type AdminConsoleUser } from '../../api/adminConsole';
import { ADMIN_CONSOLE_ME_QUERY_KEY } from '../../lib/adminSession';
import { PermissionKey } from '@c4ytechnology/authz';
import { hasAdminPermission } from '../../lib/adminPermissions';
import { messageFromOrchestratorAxiosError } from '../../lib/axiosErrorMessage';
import {
  actabilityMessage,
  assignmentActability,
} from '../../lib/manualReviewAssignment';
import { ManualReviewReassignDialog } from '../loanReview/ManualReviewReassignDialog';
import { collectManualReviewAttentionItems } from '../../lib/manualReviewPanelContent';

type Props = {
  application: ApplicationJourneyHeaderDto;
  deviceRisk?: ApplicationDeviceRiskResponseDto | null;
  /** Journey snapshot sessions — same source as the Identity tab status dot. */
  identitySessions?: unknown | null;
  onNavigate?: (key: ApplicationTabKey) => void;
};

/** Decision UI is gated by group permission; the API is the enforcement boundary. */

const money = (n: number | null | undefined): string =>
  n == null ? '—' : `$${Math.round(n).toLocaleString('en-CA')}`;

/** DE rates arrive as either a fraction (0.41) or a percent (41). Normalize to %. */
const pct = (n: number | null | undefined): string => {
  if (n == null) return '—';
  const v = n <= 1 ? n * 100 : n;
  return `${v.toFixed(1).replace(/\.0$/, '')}%`;
};
function monthlyPayment(principal: number | null | undefined, aprPct: number | null | undefined, months: number | null | undefined): number | null {
  if (!principal || !months) return null;
  const apr = aprPct == null ? 0 : aprPct > 1 ? aprPct / 100 : aprPct;
  const r = apr / 12;
  if (r === 0) return principal / months;
  return (principal * r) / (1 - Math.pow(1 + r, -months));
}

function initials(first?: string | null, last?: string | null): string {
  const a = (first ?? '').trim();
  const b = (last ?? '').trim();
  const s = `${a.charAt(0)}${b.charAt(0)}`.toUpperCase();
  return s || '—';
}

function agentDecisionLabel(decision?: string): { text: string; color: 'success' | 'warning' | 'error' } {
  switch (decision) {
    case 'APPROVE':
      return { text: 'Approve', color: 'success' };
    case 'DECLINE':
      return { text: 'Decline', color: 'error' };
    default:
      return { text: 'Needs review', color: 'warning' };
  }
}

function SectionCard({ icon, title, onOpen, openLabel, children }: {
  icon: ReactNode; title: string; onOpen?: () => void; openLabel?: string; children: ReactNode;
}) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, p: 2.25 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
        <Typography sx={{ fontSize: 15, fontWeight: 600, flex: 1 }}>{title}</Typography>
        {onOpen ? (
          <Button size="small" endIcon={<ChevronRightRoundedIcon />} onClick={onOpen} sx={{ textTransform: 'none' }}>
            {openLabel}
          </Button>
        ) : null}
      </Stack>
      {children}
    </Paper>
  );
}

export function ApplicationEmployeeDecisionPanel({ application, deviceRisk, identitySessions = null, onNavigate }: Props) {
  const queryClient = useQueryClient();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [reassignOpen, setReassignOpen] = useState(false);

  const applicationId = application.applicationId;
  const status = application.status;
  const loanReview = application.loanReview;
  const de = application.decisionEngine ?? undefined;
  const loanSummary = application.loanSummary ?? undefined;

  const meQ = useQuery<AdminConsoleUser>({
    queryKey: ADMIN_CONSOLE_ME_QUERY_KEY,
    queryFn: () => adminConsoleAuthApi.me(),
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const canAct = hasAdminPermission(meQ.data, PermissionKey.APPLICATIONS_DECIDE);
  const canReassign = hasAdminPermission(meQ.data, PermissionKey.MANUAL_REVIEW_REASSIGN);

  const assignmentQ = useQuery({
    queryKey: ['manualReviewAssignment', applicationId],
    queryFn: () => manualReviewApi.assignment(applicationId),
    enabled: status === 'MANUAL_REVIEW',
    retry: false,
    refetchOnWindowFocus: true,
  });
  const actability = assignmentActability(assignmentQ.data?.assignment ?? null, [
    meQ.data?.email,
  ]);
  const blockedMessage = actabilityMessage(actability);
  const canDecideThisFile = canAct && (actability.kind === 'none' || actability.kind === 'mine');

  const decideMutation = useMutation({
    mutationFn: (body: { decision: 'ACCEPT' | 'REJECT'; reason?: string }) =>
      applicationsApi.decide(applicationId, body),
    onSuccess: async () => {
      setRejectOpen(false);
      setReason('');
      await queryClient.invalidateQueries({ queryKey: ['applicationJourney', applicationId] });
      await queryClient.invalidateQueries({ queryKey: ['manualReviewAssignment', applicationId] });
      await queryClient.invalidateQueries({ queryKey: MANUAL_REVIEW_QUEUE_QUERY_KEY });
      // The Applications list too. A decision changes the row's status, and its key carries the
      // active filters (`['applications', queryParams]`) — so invalidate the prefix rather than
      // one filter combination, or navigating back shows the loan still sitting in MANUAL_REVIEW.
      await queryClient.invalidateQueries({ queryKey: ['applications'] });
    },
  });

  const go = (key: ApplicationTabKey) => onNavigate?.(key);

  // Already decided — compact who/what/when record for the normal decision surface.
  if (status !== 'MANUAL_REVIEW') {
    const ed = loanReview?.employeeDecision;
    if (!ed) return null;
    return (
      <Alert severity={ed.decision === 'ACCEPT' ? 'success' : 'error'} variant="outlined" data-testid="application-employee-decision-record">
        <Typography variant="body2">
          {ed.decision === 'ACCEPT' ? 'Accepted' : 'Rejected'} by <strong>{ed.actorEmail || ed.actorUserId}</strong> on{' '}
          {new Date(ed.decidedAt).toLocaleString()}
          {ed.reason ? ` — ${ed.reason}` : ''}
        </Typography>
      </Alert>
    );
  }

  const errorMessage = decideMutation.isError ? messageFromOrchestratorAxiosError(decideMutation.error) : null;

  // ── derived values (journey + device-risk only; deep-link for the rest) ──
  const firstName = de?.applicantFirstName ?? null;
  const lastName = de?.applicantLastName ?? null;
  const applicantName = `${firstName ?? ''} ${lastName ?? ''}`.trim() || 'Applicant';

  const requested = loanSummary?.requestedPrincipal ?? de?.requestedAmount ?? null;
  const eligible = de?.approvedAmount ?? de?.maxEligibleAmount ?? null;
  const termMonths = loanSummary?.loanTermMonths ?? de?.maxTerm ?? null;
  const apr = loanSummary?.interestRateApr ?? de?.interestRate ?? null;
  const monthly = monthlyPayment(eligible ?? requested, apr, termMonths);
  const termAprLabel = [termMonths ? `${termMonths} mo` : null, apr != null ? `${pct(apr)} APR` : null].filter(Boolean).join(' · ') || 'Terms';

  const milestones = new Set((application.completedMilestones ?? []).map((m) => m.toUpperCase()));
  const identitySessionItems = useMemo(() => {
    if (!identitySessions || typeof identitySessions !== 'object' || Array.isArray(identitySessions)) {
      return [] as Record<string, unknown>[];
    }
    const raw = (identitySessions as Record<string, unknown>).items;
    if (!Array.isArray(raw)) return [] as Record<string, unknown>[];
    return raw.filter(
      (row): row is Record<string, unknown> =>
        row != null && typeof row === 'object' && !Array.isArray(row),
    );
  }, [identitySessions]);
  const identityEvidencePresent = useMemo(() => {
    if (!milestones.has('IDENTITY_VERIFIED')) return false;
    const latest = identitySessionItems[identitySessionItems.length - 1];
    return latest ? identitySessionHasPersistedEvidence(latest) : false;
  }, [identitySessionItems, milestones]);
  const employerUnderReview =
    milestones.has('EMPLOYMENT_PENDING_REVIEW') || !milestones.has('EMPLOYMENT_VERIFIED');

  const reasonLine =
    application.declineReason ||
    de?.declinedReason ||
    (employerUnderReview ? 'Employment verification requires a human decision' : 'Routed to manual review');

  const agent = agentDecisionLabel(loanReview?.decision);
  const agentRationale =
    (typeof loanReview?.agentDetails?.rationale === 'string' && loanReview.agentDetails.rationale) ||
    (typeof loanReview?.agentDetails?.explanation === 'string' && loanReview.agentDetails.explanation) ||
    (typeof loanReview?.agentDetails?.recommendedOutcome === 'string'
      ? `Recommended outcome: ${loanReview.agentDetails.recommendedOutcome}`
      : null);
  const agentConfidence =
    typeof loanReview?.agentDetails?.confidence === 'number' ? loanReview.agentDetails.confidence : null;

  const reviewAttentionItems = useMemo(
    () => collectManualReviewAttentionItems(loanReview),
    [loanReview],
  );

  const deviceLevel = deviceRisk?.riskVerdict?.level ?? null;
  const priorApps = (deviceRisk?.visitorApplicationHistory ?? []).filter((h) => !h.isCurrent).length;

  const decideBtns = decided(decideMutation);

  const verification = [
    {
      icon: <FingerprintRoundedIcon fontSize="small" />,
      name: 'Identity',
      ok: identityEvidencePresent,
      facts: identityEvidencePresent
        ? 'Identity check passed'
        : milestones.has('IDENTITY_VERIFIED')
          ? 'Identity details missing'
          : 'Identity not yet verified',
      to: 'identity' as const,
    },
    {
      icon: <PaymentsRoundedIcon fontSize="small" />,
      name: 'Income',
      ok: milestones.has('INCOME_VERIFIED') || de?.monthlyIncome != null,
      facts: de?.monthlyIncome != null ? `${money(de.monthlyIncome)}/mo verified` : 'Income evidence on file',
      to: 'income' as const,
    },
    {
      icon: <BusinessRoundedIcon fontSize="small" />,
      name: 'Employer',
      ok: !employerUnderReview,
      facts: [de?.employerName, de?.occupation].filter(Boolean).join(' · ') || 'Employer verification',
      to: 'employer' as const,
    },
  ];

  return (
    <Stack spacing={2.5} data-testid="application-employee-decision-panel">
      {/* Summary + action strip */}
      <Paper variant="outlined" sx={{ borderRadius: 3, p: 2.25 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2} alignItems={{ md: 'center' }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), color: 'primary.main', width: 46, height: 46, fontWeight: 600 }}>
              {initials(firstName, lastName)}
            </Avatar>
            <Box>
              <Typography sx={{ fontSize: 17, fontWeight: 600 }}>{applicantName}</Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                {[application.ccid, application.email].filter(Boolean).join(' · ') || applicationId}
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={4}>
            <Box><Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Requested</Typography><Typography sx={{ fontSize: 19, fontWeight: 600 }}>{money(requested)}</Typography></Box>
            <Box><Typography sx={{ fontSize: 12, color: 'text.secondary' }}>Eligible</Typography><Typography sx={{ fontSize: 19, fontWeight: 600, color: 'primary.main' }}>{money(eligible)}</Typography></Box>
            <Box><Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{termAprLabel}</Typography><Typography sx={{ fontSize: 19, fontWeight: 600 }}>{monthly != null ? `${money(monthly)}/mo` : '—'}</Typography></Box>
          </Stack>
        </Stack>
        <Divider sx={{ my: 1.75 }} />
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center">
            <GavelRoundedIcon sx={{ color: 'warning.main', fontSize: 20 }} />
            <Typography sx={{ fontSize: 14 }}><b>In manual review</b> — {reasonLine}</Typography>
          </Stack>
          {errorMessage ? null : null}
          <Stack direction="row" spacing={1.5} alignItems="center">
            {canDecideThisFile ? (
              <>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircleRoundedIcon />}
                  disabled={decideMutation.isPending || !identityEvidencePresent}
                  onClick={() => decideMutation.mutate({ decision: 'ACCEPT' })}
                  data-testid="employee-decision-accept"
                >
                  {decideBtns.acceptBusy ? <CircularProgress size={18} color="inherit" /> : 'Accept'}
                </Button>
                <Button variant="outlined" color="error" startIcon={<BlockRoundedIcon />} disabled={decideMutation.isPending}
                  onClick={() => setRejectOpen(true)} data-testid="employee-decision-reject">
                  Reject
                </Button>
              </>
            ) : canAct && blockedMessage ? (
              <Typography variant="body2" color="text.secondary" data-testid="employee-decision-assigned-to">
                {blockedMessage}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">Requires the Reviewer role or higher to act.</Typography>
            )}
            {canReassign ? (
              <Button size="small" onClick={() => setReassignOpen(true)} sx={{ textTransform: 'none' }}>
                Reassign
              </Button>
            ) : null}
          </Stack>
        </Stack>
        {errorMessage ? <Alert severity="error" sx={{ mt: 1.5 }}>{errorMessage}</Alert> : null}
        {!identityEvidencePresent && milestones.has('IDENTITY_VERIFIED') ? (
          <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="employee-decision-identity-missing">
            Identity verification must be completed before this application can be approved.
          </Alert>
        ) : null}
      </Paper>

      {/* Why + AI recommendation */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2.5 }}>
        <Alert severity="warning" icon={<WarningAmberRoundedIcon />} onClick={() => go('employer')}
          sx={{ borderRadius: 3, m: 0, cursor: onNavigate ? 'pointer' : 'default', '&:hover': onNavigate ? { boxShadow: 2 } : undefined }}
          action={onNavigate ? <ChevronRightRoundedIcon sx={{ color: 'warning.main' }} /> : undefined}>
          <AlertTitle sx={{ fontWeight: 600 }}>Why this needs you</AlertTitle>
          {reasonLine}. Accepting approves the loan and lets funding proceed; rejecting declines it. The customer is emailed once you act.
        </Alert>
        <Paper variant="outlined" onClick={() => go('loan-review')}
          sx={{ borderRadius: 3, p: 2, borderColor: 'primary.main', bgcolor: (t) => alpha(t.palette.primary.main, 0.04), cursor: onNavigate ? 'pointer' : 'default', '&:hover': onNavigate ? { boxShadow: 2 } : undefined }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
            <SmartToyRoundedIcon sx={{ color: 'primary.main' }} fontSize="small" />
            <Typography sx={{ fontSize: 15, fontWeight: 600, flex: 1 }}>AI recommendation</Typography>
            <Chip size="small" color="primary" label="Advisory" sx={{ fontSize: 11 }} />
            {onNavigate ? <ChevronRightRoundedIcon sx={{ color: 'text.disabled' }} fontSize="small" /> : null}
          </Stack>
          {loanReview?.decision ? (
            <>
              <Stack direction="row" spacing={1.5} alignItems="baseline">
                <Typography sx={{ fontSize: 22, fontWeight: 600, color: `${agent.color}.main` }}>{agent.text}</Typography>
                {agentConfidence != null ? <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>confidence {agentConfidence.toFixed(2)}</Typography> : null}
              </Stack>
              {agentRationale ? <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }}>{agentRationale}</Typography> : null}
            </>
          ) : (
            <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>No agent recommendation on file.</Typography>
          )}
        </Paper>
      </Box>

      {reviewAttentionItems.length ? (
        <SectionCard
          icon={<FactCheckRoundedIcon fontSize="small" />}
          title="Checks requiring review"
          onOpen={onNavigate ? () => go('loan-review') : undefined}
          openLabel="Loan review"
        >
          <Stack spacing={1.25} data-testid="manual-review-attention-list">
            {reviewAttentionItems.map((item) => (
              <Box
                key={`${item.label}-${item.detail}`}
                onClick={item.tab && onNavigate ? () => go(item.tab!) : undefined}
                sx={{
                  border: 1,
                  borderColor: item.severity === 'error' ? 'error.main' : 'warning.main',
                  borderRadius: 2,
                  p: 1.5,
                  cursor: item.tab && onNavigate ? 'pointer' : 'default',
                  bgcolor: (t) =>
                    alpha(
                      item.severity === 'error' ? t.palette.error.main : t.palette.warning.main,
                      0.06,
                    ),
                  '&:hover':
                    item.tab && onNavigate
                      ? {
                          borderColor: 'primary.main',
                          bgcolor: (t) => alpha(t.palette.primary.main, 0.04),
                        }
                      : undefined,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Chip
                    size="small"
                    color={item.severity === 'error' ? 'error' : 'warning'}
                    variant="outlined"
                    label={item.label}
                    sx={{ fontSize: 11 }}
                  />
                  {item.tab && onNavigate ? (
                    <ChevronRightRoundedIcon sx={{ color: 'text.disabled', fontSize: 18, ml: 'auto' }} />
                  ) : null}
                </Stack>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.5 }}>
                  {item.detail}
                </Typography>
              </Box>
            ))}
          </Stack>
        </SectionCard>
      ) : null}

      {/* Verification */}
      <SectionCard icon={<BadgeRoundedIcon fontSize="small" />} title="Verification">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
          {verification.map((v) => (
            <Box key={v.name} onClick={() => go(v.to)}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5, cursor: onNavigate ? 'pointer' : 'default', '&:hover': onNavigate ? { borderColor: 'primary.main', bgcolor: (t) => alpha(t.palette.primary.main, 0.04) } : undefined }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                <Box sx={{ color: 'text.secondary', display: 'flex' }}>{v.icon}</Box>
                <Typography sx={{ fontSize: 14, fontWeight: 500, flex: 1 }}>{v.name}</Typography>
                <Chip size="small" color={v.ok ? 'success' : 'warning'} variant={v.ok ? 'filled' : 'outlined'} label={v.ok ? 'Passed' : 'Manual review'} sx={{ fontSize: 11 }} />
              </Stack>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', lineHeight: 1.5 }}>{v.facts}</Typography>
            </Box>
          ))}
        </Box>
      </SectionCard>

      {/* Risk & fraud */}
      {deviceRisk ? (
        <SectionCard icon={<ShieldRoundedIcon fontSize="small" />} title="Risk & fraud" onOpen={onNavigate ? () => go('device') : undefined} openLabel="Device intelligence">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
            <Box onClick={() => go('device')} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5, cursor: onNavigate ? 'pointer' : 'default', '&:hover': onNavigate ? { borderColor: 'primary.main' } : undefined }}>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 0.75 }}>Device / visitor risk</Typography>
              <Chip size="small" color={deviceLevel === 'blocked' ? 'error' : deviceLevel === 'clear' ? 'success' : 'warning'} variant="outlined" label={deviceLevel ? deviceLevel[0].toUpperCase() + deviceLevel.slice(1) : 'Unknown'} sx={{ fontSize: 11 }} />
            </Box>
            <Box onClick={() => go('device')} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5, cursor: onNavigate ? 'pointer' : 'default', '&:hover': onNavigate ? { borderColor: 'primary.main' } : undefined }}>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 0.75 }}>Prior applications (same device)</Typography>
              <Chip size="small" color={priorApps > 0 ? 'warning' : 'success'} variant="outlined" label={priorApps > 0 ? `${priorApps} prior` : 'None'} sx={{ fontSize: 11 }} />
            </Box>
            <Box onClick={() => go('device')} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.5, cursor: onNavigate ? 'pointer' : 'default', '&:hover': onNavigate ? { borderColor: 'primary.main' } : undefined }}>
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 0.75 }}>Device review flag</Typography>
              <Chip size="small" color={deviceRisk.deviceRiskReview ? 'warning' : 'success'} variant="outlined" label={deviceRisk.deviceRiskReview ? 'Flagged' : 'Clear'} sx={{ fontSize: 11 }} />
            </Box>
          </Box>
          {deviceRisk.riskVerdict?.reasons?.length ? (
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 1.25 }}>{deviceRisk.riskVerdict.reasons.join(' · ')}</Typography>
          ) : null}
        </SectionCard>
      ) : null}

      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Reject loan</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              A reason is required. It is recorded on the audit trail; it is not shown to the customer.
            </Typography>
            <TextField label="Reason for rejection" multiline minRows={2} value={reason} onChange={(e) => setReason(e.target.value)} autoFocus required data-testid="employee-decision-reject-reason" />
            {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)} disabled={decideMutation.isPending}>Cancel</Button>
          <Button variant="contained" color="error" disabled={decideMutation.isPending || reason.trim().length === 0}
            onClick={() => decideMutation.mutate({ decision: 'REJECT', reason: reason.trim() })} data-testid="employee-decision-reject-confirm">
            {decideMutation.isPending ? <CircularProgress size={18} color="inherit" /> : 'Confirm rejection'}
          </Button>
        </DialogActions>
      </Dialog>
      {reassignOpen ? (
        <ManualReviewReassignDialog
          applicationId={applicationId}
          open
          onClose={() => setReassignOpen(false)}
        />
      ) : null}
    </Stack>
  );
}

function decided(m: { isPending: boolean; variables?: { decision: 'ACCEPT' | 'REJECT' } }) {
  return { acceptBusy: m.isPending && m.variables?.decision === 'ACCEPT' };
}
